import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { TodayPerformance, todayWindow } from '../cms/todayData';
import { GET_TINYCMS_ANALYTICS } from '../../graphql/queries';
import { InkSkeleton } from '../cms/InkSkeleton';
import { RollNumber } from '../common/RollNumber';
import {
  chartBars,
  Days,
  Ghost,
  GhostChart,
  GhostTile,
  Legend,
  RangeSwitch,
  Soon,
  TipBars,
  countOf,
} from '../cms/analyticsParts';
import { Channel, channelName, channelStatCells, REACH_CHANNEL_ROWS, toDate } from './reach';
import {
  compact,
  REACH_SITE_VISITS,
  ReachVisits,
  reportsViews,
  viewsShown,
  visitBars,
  visitChange,
  visitSources,
} from './reachVisits';
import { updatedWhen } from '../cms/analyticsMath';

/**
 * Reach → Analytics ("TinyCMS UX optimization review", App Prototype → Reach
 * analytics): followers, impressions, visits to the site and sign-ups from
 * social, a chart of visits by channel, a row per channel, and the posts that
 * sent the most visits. Followers and their 30-day change are real (the
 * engine's daily snapshot, the same numbers as Channels). Visits to the
 * site are real once the site's Google Analytics is connected: every post's
 * link carries the post's id, so they add up per post and per channel
 * (reachVisits.ts). Impressions are the views each network reports for our
 * posts (Facebook Pages, Instagram connected directly or through a Page,
 * Threads, YouTube; X when switched on), from the engine's post.metrics. A network that reports none
 * shows "—"; nothing reported at all is a grey shape, never a made-up number
 * or a "Coming soon" note.
 */

/** Which networks' views count toward Impressions, in plain words. */
const VIEWS_NOTE =
  'Impressions are the views each network reports for your posts: Facebook Pages, Instagram, Threads and YouTube. Other networks show —.';

/**
 * Each channel's colour in the chart key and table: one fixed slot per network
 * (ink.css --ink-series-*), seven for the seven networks, so no two share a
 * colour (backlog 77a880df: Facebook and YouTube were both black, Threads a
 * faint grey).
 */
const COLORS = [1, 2, 3, 4, 5, 6, 7].map((n) => `var(--ink-series-${n})`);

/** "X, Threads and LinkedIn". */
const listed = (names: string[]) =>
  names.length < 2
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export function ReachAnalytics() {
  const { accountId } = useAccount();
  const [days, setDays] = useState<Days>(7);
  // Today's window, so "didn't go out" counts what Today and Channels list.
  const since = todayWindow('week').from;
  const { data, loading } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNEL_ROWS, {
    variables: { accountId, since },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // Connected channels first; a disconnected one (Disconnect keeps the row,
  // switched off) keeps its history below them, tagged, and counts toward
  // nothing that says "connected".
  const channels = [...(data?.socialProfiles || [])].sort(
    (a, b) => Number(b.isActive) - Number(a.isActive)
  );
  const connected = channels.filter((c) => c.isActive);
  // No channel yet: the page keeps its shape in grey under a card that says how
  // to fill it, as Content → Analytics does before Google Analytics is connected.
  const noChannels = Boolean(!loading && data && !connected.length);
  const unit = chartBars(days).unit;
  const { data: visitData } = useQuery<{ reachSiteVisits: ReachVisits }>(REACH_SITE_VISITS, {
    variables: { accountId, days },
    skip: !accountId || noChannels,
    fetchPolicy: 'cache-and-network',
  });
  const v = visitData?.reachSiteVisits;
  const visits = !noChannels && v && v.days === days ? v : null;
  // Numbers only once the first sync has landed; before that, grey.
  const live = Boolean(visits?.connected && visits.syncedAt);
  // Content → Analytics' "From social" for the same range: every visitor
  // Google Analytics puts in a social channel, however the link was shared.
  // Here only the links in posts count, so the gap gets a sentence rather
  // than a silent 15 there and 0 here (backlog cae2a3d8). Same query and
  // variables as that screen, so the two share one cached answer.
  const { data: siteData } = useQuery<{ tinycmsAnalytics: TodayPerformance }>(
    GET_TINYCMS_ANALYTICS,
    {
      variables: { accountId, days },
      skip: !accountId || !live,
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    }
  );
  const site = siteData?.tinycmsAnalytics;
  const socialVisitors = (site?.days === days && site.website?.ranges?.social?.count) || 0;
  // Visits that named no post count toward the gap's explanation already.
  const otherSocial = live && visits && socialVisitors > visits.visits.count ? socialVisitors : 0;
  // Without Google Analytics, the grey visit parts say how to get them; while
  // the first sync runs, that it's running. Otherwise no tag at all.
  const notConnected = Boolean(visits && !visits.connected);
  const ghostTag = notConnected
    ? 'Google Analytics'
    : visits?.connected && !visits.syncedAt
      ? 'Connected'
      : null;
  const ghostWhat = notConnected ? (
    <Link to="/content?view=analytics" className="today-soon-link">
      Connect to see visits →
    </Link>
  ) : (
    'Bringing in your numbers'
  );

  // Followers across the channels that report a count, and their 30-day change.
  const counted = connected.filter((c) => c.stats);
  const followers = counted.reduce((n, c) => n + (c.stats?.followers || 0), 0);
  const changes = counted.filter((c) => c.stats?.change30d != null);
  const change = changes.reduce((n, c) => n + (c.stats?.change30d || 0), 0);
  const asOf = counted
    .map((c) => toDate(c.stats?.asOf))
    .filter((d): d is Date => Boolean(d))
    .sort((a, b) => b.getTime() - a.getTime())[0];
  const names = [...new Set(connected.map((c) => channelName(c.platform)))];
  // The chart's networks: connected channels first, then any other network
  // that sent visits (a channel since disconnected), each with its own colour.
  const sources = live && visits ? visitSources(visits.daily) : [];
  const keyNames = [...new Set([...names, ...sources.map(channelName)])];
  // A network outside the key (a disconnected channel that sent nothing in
  // this range) gets no series colour: its row's mark is grey.
  const colorOf = (name: string) => {
    const at = keyNames.indexOf(name);
    return at < 0 ? 'var(--ink-line3)' : COLORS[at % COLORS.length];
  };
  const byChannel = new Map((visits?.channels || []).map((c) => [c.socialProfileId, c.visits]));
  const signupsBy = new Map((visits?.channels || []).map((c) => [c.socialProfileId, c.signups]));
  const viewsBy = new Map((visits?.channelViews || []).map((c) => [c.socialProfileId, c.views]));
  const viewsReported = Boolean(visits?.viewsReported);
  // Views can be reported here, even if none were in this range: 0, not grey.
  const showViews = viewsShown(visits);
  const untracked = (visits?.untracked || []).reduce((n, u) => n + u.visits, 0);
  const untrackedSignups = (visits?.untracked || []).reduce((n, u) => n + (u.signups || 0), 0);
  // "5 more visits and 1 sign-up came from social links that don't name one of your posts".
  const untrackedWords = [
    untracked ? `${untracked.toLocaleString()} more ${untracked === 1 ? 'visit' : 'visits'}` : '',
    untrackedSignups
      ? `${untracked ? '' : `${untrackedSignups.toLocaleString()} more `}${untracked ? `${untrackedSignups.toLocaleString()} ` : ''}${untrackedSignups === 1 ? 'sign-up' : 'sign-ups'}`
      : '',
  ]
    .filter(Boolean)
    .join(' and ');

  return (
    <div className="an-page an-page-pad" data-screen-label="Social analytics">
      <div className="an-top">
        <RangeSwitch days={days} onChange={setDays} />
        {names.length ? (
          <span className="an-source">
            Followers from {listed(names)}
            {asOf
              ? ` · updated ${asOf.toLocaleDateString([], { month: 'short', day: 'numeric' })}`
              : ''}
            {live && visits
              ? ` · Visits from Google Analytics, ${updatedWhen(visits.syncedAt)}`
              : ''}
          </span>
        ) : noChannels ? (
          <span className="an-source">No channels connected</span>
        ) : null}
      </div>

      {noChannels ? (
        <div className="an-card an-connect">
          <div className="an-connect-title">See how your posts do</div>
          <p className="an-connect-copy">
            Connect a channel, like X, Threads or LinkedIn, and its followers, impressions, visits
            to your site and your top posts show here and on Today.
          </p>
          <div className="an-connect-actions">
            <Link to="/reach/channels" className="ink-btn ink-btn-primary">
              Connect a channel
            </Link>
          </div>
        </div>
      ) : null}

      <div className="an-tiles">
        {noChannels ? (
          <GhostTile label="Followers" hint="Total across your connected channels, right now" />
        ) : (
          <div className="an-tile" title="Total across your connected channels, right now">
            <span className="an-tile-label">Followers</span>
            <span className="an-tile-value">
              {loading && !data ? (
                <Ghost width={96} height={26} />
              ) : counted.length ? (
                <RollNumber value={followers} />
              ) : (
                '—'
              )}
            </span>
            <span className="an-tile-sub">
              {changes.length ? (
                <>
                  <span data-tone={change < 0 ? 'down' : 'up'}>
                    {change > 0 ? '+' : change < 0 ? '−' : ''}
                    {Math.abs(change).toLocaleString()}
                  </span>{' '}
                  in 30 days
                </>
              ) : (
                'Counted once a day'
              )}
            </span>
          </div>
        )}
        {visits && showViews ? (
          <CountTile
            label="Impressions"
            hint="Times your posts were seen, as each network counts them. Meta and X call these views."
            value={visits.impressions}
            days={days}
          />
        ) : (
          <GhostTile
            label="Impressions"
            hint="Times your posts were seen. Meta and X call these views."
          />
        )}
        {live && visits ? (
          <CountTile
            label="Visits to your site"
            hint="Clicks on links in your posts that reached your site"
            value={visits.visits}
            days={days}
          />
        ) : (
          <GhostTile
            label="Visits to your site"
            hint="Clicks on links in your posts that reached your site"
          />
        )}
        {visits ? (
          <CountTile
            label="Sign-ups from social"
            hint="Form sign-ups by visitors who came from a post"
            value={visits.signups}
            days={days}
          />
        ) : (
          <GhostTile
            label="Sign-ups from social"
            hint="Form sign-ups by visitors who came from a post"
          />
        )}
      </div>

      <div className="an-card an-chart">
        <div className="an-card-head">
          <div>
            <div className="an-card-title">Visits to your site from social</div>
            <div className="an-card-sub">
              Each bar is one {unit}, split by channel. Point at or tap a bar for its numbers.
            </div>
          </div>
          <Legend items={keyNames.map((n) => [n, colorOf(n)])} />
        </div>
        {live && visits ? (
          <VisitChart
            visits={visits}
            days={days}
            colorOf={colorOf}
            keyOrder={(source) => keyNames.indexOf(channelName(source))}
          />
        ) : (
          <GhostChart days={days} tag={ghostTag} what={ghostWhat} />
        )}
        {otherSocial ? (
          <p className="an-card-foot">
            Google Analytics counted {otherSocial.toLocaleString()} social{' '}
            {otherSocial === 1 ? 'visitor' : 'visitors'} in this range (
            <Link to="/content?view=analytics">Website → Analytics</Link>
            ). Only clicks on the links in your posts count here, so the rest came from links
            without a post&rsquo;s tag: shared by hand, by other people, or from your profiles.
          </p>
        ) : null}
      </div>

      <div className="an-card an-table-card">
        <div className="an-table">
          <div className="an-table-head">
            <span>Channel</span>
            <span>Followers</span>
            <span>30-day change</span>
            <span>Impressions</span>
            <span>Visits to site</span>
            <span>Sign-ups</span>
          </div>
          {loading && !data ? (
            <InkSkeleton rows={3} label="Loading channels" />
          ) : noChannels ? (
            [92, 76, 84].map((w, i) => (
              <div key={i} className="an-table-row" aria-hidden="true">
                <div className="an-chan">
                  <Ghost width={w} height={11} />
                </div>
                <span>
                  <Ghost width={40} height={10} />
                </span>
                <span>
                  <Ghost width={32} height={10} />
                </span>
                <span>
                  <Ghost width={44} height={10} />
                </span>
                <span>
                  <Ghost width={36} height={10} />
                </span>
                <span>
                  <Ghost width={20} height={10} />
                </span>
              </div>
            ))
          ) : (
            channels.map((c) => {
              const stat = channelStatCells(c.stats);
              const color = colorOf(channelName(c.platform));
              const gone = !c.isActive;
              const missed = gone ? 0 : c.missedPosts?.count || 0;
              const who = c.profileName || (c.profileUsername ? `@${c.profileUsername}` : '');
              const goneOn = gone ? toDate(c.updatedAt) : null;
              return (
                <div key={c.id} className="an-table-row" data-gone={gone || undefined}>
                  <div className="an-chan">
                    <span className="an-chan-name">
                      <i style={{ background: color }} />
                      {channelName(c.platform)}
                      {who ? (
                        <span className="an-chan-who" title={who}>
                          {who}
                        </span>
                      ) : null}
                    </span>
                    {/* Under the name, so it never squeezes the account name
                        to "I" (QA round 23). */}
                    {gone ? (
                      <span
                        className="an-chan-gone"
                        title="No longer connected to this site. Its past numbers stay here."
                      >
                        Disconnected
                        {goneOn
                          ? ` ${goneOn.toLocaleDateString([], { month: 'short', day: 'numeric' })}`
                          : ''}
                      </span>
                    ) : null}
                    {missed ? (
                      <Link to={`/reach/channels?review=${c.id}`} className="an-chan-note">
                        {missed === 1 ? '1 post' : `${missed} posts`} didn’t go out this week.
                        Review →
                      </Link>
                    ) : null}
                  </div>
                  <span>{stat.followers}</span>
                  <span data-tone={stat.growthTone}>{stat.growth}</span>
                  {showViews ? (
                    <span
                      title={
                        viewsBy.has(c.id) || reportsViews(visits, c.platform)
                          ? 'Views of this channel’s posts in this range'
                          : 'Not reported by this network'
                      }
                    >
                      {viewsBy.has(c.id)
                        ? compact(viewsBy.get(c.id) || 0)
                        : reportsViews(visits, c.platform)
                          ? '0'
                          : '—'}
                    </span>
                  ) : (
                    <span>
                      <Ghost width={44} height={10} />
                    </span>
                  )}
                  {live ? (
                    <span>{(byChannel.get(c.id) || 0).toLocaleString()}</span>
                  ) : (
                    <span>
                      <Ghost width={36} height={10} />
                    </span>
                  )}
                  {visits ? (
                    <span>{(signupsBy.get(c.id) || 0).toLocaleString()}</span>
                  ) : (
                    <span>
                      <Ghost width={20} height={10} />
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="an-card an-posts">
        <div className="an-card-head">
          <div className="an-card-title">
            {/* Without visits it ranks by views, so it says so. */}
            {live || !viewsReported ? 'Posts that sent the most visits' : 'Posts seen the most'}
          </div>
          <span className="an-card-sub">
            {live || viewsReported
              ? [viewsReported ? 'Views' : '', live ? 'visits' : '', 'sign-ups']
                  .filter(Boolean)
                  .join(' · ')
                  .replace(/^./, (c) => c.toUpperCase())
              : 'Impressions · clicks · sign-ups'}
          </span>
        </div>
        {(live || viewsReported) && visits ? (
          <TopPosts posts={visits.posts} colorOf={colorOf} visitsLive={live} />
        ) : (
          <div className="an-soon">
            <div className="an-rows" aria-hidden="true">
              {[86, 72, 64].map((w, i) => (
                <div key={i} className="an-post-row">
                  <span>
                    <span className="ink-ghost" style={{ width: `${w}%`, height: 11 }} />
                    <span className="ink-ghost" style={{ width: '22%', height: 9, marginTop: 8 }} />
                  </span>
                  <span className="an-post-nums">
                    <Ghost width={56} height={10} />
                    <Ghost width={48} height={10} />
                    <Ghost width={52} height={10} />
                  </span>
                </div>
              ))}
            </div>
            {ghostTag ? <Soon tag={ghostTag}>{ghostWhat}</Soon> : null}
          </div>
        )}
      </div>

      <div className="an-note">
        {noChannels ? (
          'Followers and impressions come from each channel you connect. Visits and sign-ups are counted by your site from the links in your posts.'
        ) : live ? (
          <>
            Followers come from each network, which counts them in its own way, and are collected
            once a day. Visits are counted by your site&rsquo;s Google Analytics from the link in
            each post, so every channel is measured the same way. Sign-ups come from your
            site&rsquo;s forms, sent by visitors who came from a post.
            {untrackedWords
              ? ` ${untrackedWords} came from social links that don’t name one of your posts (posts from before visits were tracked, or links tagged by hand).`
              : ''}
            {viewsReported ? ` ${VIEWS_NOTE}` : ''}
          </>
        ) : visits && !visits.connected ? (
          <>
            Followers come from each network and are collected once a day. To see which posts send
            people to your site, <Link to="/content?view=analytics">connect Google Analytics</Link>.
            {viewsReported ? ` ${VIEWS_NOTE}` : ''}
          </>
        ) : (
          `Followers come from each network, which counts them in its own way, and are collected once a day.${viewsReported ? ` ${VIEWS_NOTE}` : ''}`
        )}
      </div>
    </div>
  );
}

const CountTile: React.FC<{
  label: string;
  hint: string;
  value: { count: number; previous: number };
  days: number;
}> = ({ label, hint, value, days }) => {
  const delta = visitChange(value, days);
  return (
    <div className="an-tile" title={hint}>
      <span className="an-tile-label">{label}</span>
      <span className="an-tile-value">
        <RollNumber value={value.count} />
      </span>
      <span className="an-tile-sub">
        {delta.lead ? <span data-tone={delta.tone}>{delta.lead}</span> : null}
        {delta.rest}
      </span>
    </div>
  );
};

/** Visits per day (per week over 90 days), stacked by network. */
const VisitChart: React.FC<{
  visits: ReachVisits;
  days: Days;
  colorOf: (name: string) => string;
  /** A source's place in the chart key: the bar stacks in that order. */
  keyOrder: (source: string) => number;
}> = ({ visits, days, colorOf, keyOrder }) => {
  const bars = visitBars(visits.daily, days);
  const max = Math.max(1, ...bars.map((b) => b.total));
  return (
    <>
      <TipBars
        label="Visits to your site from social"
        gap={days === 7 ? 16 : days === 28 ? 4 : 8}
        bars={bars.map((b) => {
          // Stacked in the key's order every day, so neighbouring colours stay
          // the validated pairs and a network keeps its place in the bar.
          const parts = Object.entries(b.bySource).sort((x, y) => keyOrder(x[0]) - keyOrder(y[0]));
          return {
            key: b.day,
            title: b.label,
            total: countOf(b.total, 'visit'),
            rows: parts.map(([src, n]) => ({
              label: channelName(src),
              value: n.toLocaleString(),
              color: colorOf(channelName(src)),
            })),
            segments: [...parts].reverse().map(([src, n]) => ({
              key: src,
              height: Math.max(2, Math.round((n / max) * 168)),
              color: colorOf(channelName(src)),
            })),
          };
        })}
      />
      <div className="an-x">
        <span>{bars.length ? bars[0].label : ''}</span>
        <span>{bars.length > 2 ? bars[Math.floor(bars.length / 2)].label : ''}</span>
        <span>{days === 90 && bars.length ? bars[bars.length - 1].label : 'Today'}</span>
      </div>
    </>
  );
};

const postDay = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';

/** The posts that sent the most visits, with where and when they went out. */
const TopPosts: React.FC<{
  posts: ReachVisits['posts'];
  colorOf: (name: string) => string;
  visitsLive: boolean;
}> = ({ posts: all, colorOf, visitsLive }) => {
  // Ranked by visits: a post that sent none isn't one of them, even if it was
  // seen in the range (QA round 23: "hello world · 12 views · 0 visits").
  const posts = visitsLive ? all.filter((p) => p.visits > 0 || p.signups > 0) : all;
  return posts.length ? (
    <div className="an-rows">
      {posts.map((p) => (
        <div key={p.postId} className="an-post-row">
          <span className="an-row-main">
            <span className="an-row-title">{p.text || 'A post with no text'}</span>
            <span className="an-row-sub">
              <i className="an-post-dot" style={{ background: colorOf(channelName(p.platform)) }} />
              {channelName(p.platform)}
              {p.at ? ` · ${postDay(p.at)}` : ''}
              {p.engagements
                ? ` · ${compact(p.engagements)} ${p.engagements === 1 ? 'engagement' : 'engagements'}`
                : ''}
            </span>
          </span>
          <span className="an-row-n">
            {[
              p.views != null ? `${compact(p.views)} ${p.views === 1 ? 'view' : 'views'}` : '',
              visitsLive
                ? `${p.visits.toLocaleString()} ${p.visits === 1 ? 'visit' : 'visits'}`
                : '',
              p.signups
                ? `${p.signups.toLocaleString()} ${p.signups === 1 ? 'sign-up' : 'sign-ups'}`
                : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      ))}
    </div>
  ) : (
    <p className="an-empty-line">
      {visitsLive
        ? 'No visits came from your posts in this range. Links in posts that go out from now on say which post they came from.'
        : 'None of your posts were seen in this range.'}
    </p>
  );
};
